// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { NotFoundErrorBoundary } from '@/components/not-found-error-boundary';
import { ShipmentDetail } from '@/shipment/components/shipment-detail';
import { ShipmentDetailsSkeleton } from '@/shipment/components/shipment-details-skeleton';
import { useShipmentSuspense } from '@/shipment/hooks/use-shipment';
import { createFileRoute } from '@tanstack/react-router';
import { Suspense } from 'react';
import { z } from 'zod';

/**
 * Search schema for shipment detail page.
 * The type parameter is used to maintain navbar active state.
 */
const shipmentDetailSearchSchema = z.object({
  type: z.enum(['inbound', 'outbound']).default('outbound').catch('outbound'),
  tab: z
    .enum(['line-items', 'quarantine'])
    .default('line-items')
    .catch('line-items'),
});

export const Route = createFileRoute('/_app/shipments/$shipmentKey')({
  component: ShipmentRoute,
  validateSearch: shipmentDetailSearchSchema,
});

function ShipmentRoute() {
  const { shipmentKey } = Route.useParams();

  return (
    <NotFoundErrorBoundary
      entityType="shipment"
      listPath="/shipments"
      listLabel="Go to shipments"
    >
      <Suspense fallback={<ShipmentDetailsSkeleton />}>
        <ShipmentContent shipmentKey={shipmentKey} />
      </Suspense>
    </NotFoundErrorBoundary>
  );
}

function ShipmentContent(props: { shipmentKey: string }) {
  const { shipment } = useShipmentSuspense(props.shipmentKey);

  return <ShipmentDetail shipment={shipment} shipmentKey={props.shipmentKey} />;
}
